import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@radix-ui/themes';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import type { StoreDrift } from 'api/sectorAlignmentApi';
import { useDeskInfo } from 'hooks/useSectorAlignment';
import { fromSectorsLabel, list, STORES_GRID, type Tagged } from './rows';
import TicketRow from './TicketRow';

const SHOWN = 3;

const StoreRow = ({ drift }: { drift: Tagged<StoreDrift> }) => {
  const { t } = useTranslation();
  const deskName = useDeskInfo()(drift.sd).name;
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const panelId = useId();
  const tickets = list(drift.tickets);
  const fromSectors = fromSectorsLabel(drift.fromSectors);

  return (
    <div className="border-b last:border-b-0">
      {/* The button's click (Enter/Space included) bubbles here; the whole row stays clickable. */}
      <div role="row" onClick={() => setOpen((o) => !o)} className={cn(STORES_GRID, 'h-[52px] cursor-pointer')}>
        <span role="cell" className="min-w-0">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            className="flex max-w-full items-center gap-2 rounded-sm text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} aria-hidden="true" />
            <Tooltip content={drift.store}>
              <span className="truncate text-[13.5px] font-medium">{drift.store || '—'}</span>
            </Tooltip>
          </button>
        </span>
        <span role="cell" className="truncate text-[13px] text-muted-foreground" title={deskName}>{deskName}</span>
        <span role="cell" className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[13px]">
          <span tabIndex={0} className="min-w-0 overflow-x-auto whitespace-nowrap rounded-sm text-muted-foreground [scrollbar-width:thin] focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" title={fromSectors}>{fromSectors}</span>
          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-zinc-400 dark:text-zinc-500" role="img" aria-label={t('to')} />
          <span className="shrink-0 font-medium">{drift.currentSector || '—'}</span>
        </span>
        <span role="cell" className="text-right text-[13px] font-medium tabular-nums">{tickets.length}</span>
      </div>
      {open && (
        <div id={panelId} className="bg-zinc-50 dark:bg-zinc-950">
          {(all ? tickets : tickets.slice(0, SHOWN)).map((ticket) => <TicketRow key={ticket.key} ticket={ticket} />)}
          {!all && tickets.length > SHOWN && (
            <div role="row" className="border-t py-2.5 pl-[50px]">
              <span role="cell">
                <Button variant="outline" size="sm" className="h-[30px]" onClick={() => setAll(true)}>
                  {t('See the {{count}} other tickets', { count: tickets.length - SHOWN })}
                </Button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreRow;
